/**
 * My attendance — the employee's own month, only as HR approved it (spec §4.5, §4.6).
 * Nothing before HR approval; pay amounts only after the HR manager releases them.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Fingerprint, Loader2, CheckCircle2, MessageSquareWarning } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import TimesheetSheet from './TimesheetSheet';
import { TS_STATUS, api, errorText, hm, label, thisMonth } from './shared';

export default function MyAttendancePage() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);
  const [month, setMonth] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7);   // last month by default
  });
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await api(token).get(`/my/timesheets/${month}`);
      setData(d);
      setSheet(d.published ? await api(token).get(`/my/sheets/${month}`) : null);
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, [token, month]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    try { await api(token).post(`/my/timesheets/${month}/confirm`); toast.success(t('Thank you, confirmed.', 'شكراً، تم التأكيد.')); load(); }
    catch (e) { toast.error(errorText(e)); }
  };

  const s = data?.summary;
  return (
    <div className="p-6 space-y-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid="my-attendance-page">
      <div className="flex items-center gap-3">
        <Fingerprint className="h-7 w-7 text-brand-orange" />
        <h1 className="text-2xl font-bold text-slate-900">{t('My attendance', 'حضوري')}</h1>
        <Input type="month" value={month} max={thisMonth()} onChange={(e) => setMonth(e.target.value)} className="w-40 ms-auto"
          data-testid="my-month" />
      </div>
      {error && <div className="text-sm text-rose-600">{error}</div>}
      {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}

      {data && !data.published && (
        <div className="brand-empty-state p-8 text-center text-slate-500" data-testid="my-not-published">
          {t('HR has not approved your timesheet for this month yet. You will see it here once approved.',
            'لم يعتمد قسم الموارد البشرية كشف دوامك لهذا الشهر بعد. سيظهر هنا بعد الاعتماد.')}
        </div>
      )}

      {data?.published && (
        <>
          <Card>
            <CardContent className="py-4 flex flex-wrap items-center gap-4">
              <span className={`text-xs rounded-full px-2 py-0.5 ${TS_STATUS[data.status]?.cls || ''}`}>{label(TS_STATUS, data.status, isRTL)}</span>
              {s && <span className="text-sm">{t('Worked', 'العمل')} <b>{hm(s.worked_minutes)}</b> · {t('Overtime', 'الإضافي')} <b>{hm(s.overtime_minutes)}</b> · {t('Payable', 'المستحق')} <b>{s.payable_overtime_hours} {t('h', 'س')}</b>
                {s.absent_unexcused_days > 0 && <> · {t('Unexcused absence', 'غياب بدون عذر')} <b>{s.absent_unexcused_days}</b></>}</span>}
              {data.status === 'hr_approved' && (
                <div className="flex flex-wrap items-center gap-2 ms-auto">
                  <span className="text-xs text-slate-500">{t('Please check by', 'يرجى المراجعة قبل')} {String(data.review_deadline).slice(0, 10)}</span>
                  <Button size="sm" onClick={confirm} data-testid="my-confirm"><CheckCircle2 className="h-4 w-4 me-1" />{t('Confirm', 'تأكيد')}</Button>
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/requests"><MessageSquareWarning className="h-4 w-4 me-1" />{t('Something is wrong', 'يوجد خطأ')}</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          {data.status === 'hr_approved' && (
            <p className="text-xs text-slate-500">{t('If a day is wrong, open Requests › New › Timesheet review, choose the day and explain. No answer within the review window counts as confirmed.',
              'إذا كان هناك يوم خاطئ، افتح الطلبات › جديد › مراجعة كشف الدوام، واختر اليوم واشرح. عدم الرد خلال المهلة يُعد تأكيداً.')}</p>
          )}
          {data.disputes?.length > 0 && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
              {data.disputes.map((d) => <div key={d.id}>{d.day}: {d.details} — {d.status}</div>)}
            </div>
          )}

          {sheet && <TimesheetSheet token={token} isRTL={isRTL} data={sheet} exportPath={`/my/sheets/${month}/xlsx`} />}
        </>
      )}
    </div>
  );
}
